import { useParams, Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { getOfferBySlug } from '../data/offers';
import { FiCheck, FiX, FiExternalLink, FiArrowLeft, FiStar, FiClock, FiShield, FiSmartphone, FiDollarSign } from 'react-icons/fi';

export const OfferReview: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const offer = slug ? getOfferBySlug(slug) : undefined;

  if (!offer) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Offer Not Found</h1>
          <Link to="/" className="btn-primary">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Organization',
      name: offer.name,
      url: offer.officialUrl
    },
    author: {
      '@type': 'Organization',
      name: 'Your Casinos'
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: offer.rating10,
      bestRating: 10
    },
    reviewBody: `Comprehensive review of ${offer.name}. ${offer.bonusHeadline}. ${offer.payoutHeadline}.`
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://yourscasinos.com'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Compare',
        item: 'https://yourscasinos.com/compare'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: offer.name,
        item: `https://yourscasinos.com/offers/${offer.slug}`
      }
    ]
  };

  const scoreItems = [
    { key: 'payoutSpeed', label: 'Payout Speed', icon: FiClock, color: 'text-primary-green' },
    { key: 'gameVariety', label: 'Game Variety', icon: FiStar, color: 'text-primary-green' },
    { key: 'bonusValue', label: 'Bonus Value', icon: FiDollarSign, color: 'text-primary-green' },
    { key: 'trustScore', label: 'Trust Score', icon: FiShield, color: 'text-primary-green' },
    { key: 'mobileExperience', label: 'Mobile Experience', icon: FiSmartphone, color: 'text-primary-green' },
  ];

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-primary-green';
    if (score >= 80) return 'text-yellow-400';
    return 'text-gray-400';
  };

  return (
    <>
      <SEO
        title={`${offer.name} Review | ${offer.bonusHeadline} | Your Casinos`}
        description={`${offer.name} review: ${offer.bonusHeadline}. ${offer.payoutHeadline}. Rating: ${offer.rating10}/10. Read our full analysis.`}
        canonical={`/offers/${offer.slug}`}
        jsonLd={[jsonLd, breadcrumbJsonLd]}
      />

      <div className="min-h-screen bg-black">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-gray-900 via-black to-black py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,255,0,0.1),transparent_70%)]"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/compare"
              className="inline-flex items-center text-primary-green hover:text-primary-lightgreen mb-8 transition-colors"
            >
              <FiArrowLeft className="mr-2" />
              Back to Compare
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
              {/* Left Column - Logo and Rating */}
              <div className="lg:col-span-1">
                <div className="glass-card p-8 text-center sticky top-24">
                  <img
                    src={offer.logo}
                    alt={offer.name}
                    className="h-24 w-auto mx-auto mb-6"
                    loading="eager"
                  />
                  <div className="mb-4">
                    <div className="text-5xl font-bold text-primary-green mb-2">
                      {offer.rating10}/10
                    </div>
                    <div className="flex justify-center gap-1">
                      {[...Array(10)].map((_, i) => (
                        <FiStar
                          key={i}
                          className={`${
                            i < Math.round(offer.rating10)
                              ? 'fill-primary-green text-primary-green'
                              : 'text-gray-600'
                          }`}
                          size={20}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400 mb-6">
                    Minimum Deposit: <span className="text-white font-bold">£{offer.minDepositGbp}</span>
                  </div>
                  <a
                    href={offer.officialUrl}
                    target="_blank"
                    rel="noreferrer noopener sponsored"
                    className="btn-primary w-full inline-flex items-center justify-center mb-3"
                  >
                    Visit Official Site
                    <FiExternalLink className="ml-2" />
                  </a>
                  <Link
                    to="/compare"
                    className="btn-secondary w-full inline-flex items-center justify-center"
                  >
                    Compare with Others
                  </Link>
                </div>
              </div>

              {/* Right Column - Main Content */}
              <div className="lg:col-span-2">
                <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                  {offer.name} Review
                </h1>
                <div className="space-y-4 mb-8">
                  <div className="glass-card p-6 border-l-4 border-primary-green">
                    <h2 className="text-2xl font-bold text-primary-green mb-2">
                      {offer.bonusHeadline}
                    </h2>
                    <p className="text-gray-300">{offer.bonusHeadline}</p>
                  </div>
                  <div className="glass-card p-6 border-l-4 border-primary-green">
                    <h2 className="text-2xl font-bold text-primary-green mb-2">
                      {offer.payoutHeadline}
                    </h2>
                    <p className="text-gray-300">{offer.payoutHeadline}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats Bar */}
        <section className="bg-gray-900 border-y border-primary-green/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {scoreItems.map(({ key, label, icon: Icon }) => {
                const score = offer.scores[key as keyof typeof offer.scores];
                return (
                  <div key={key} className="text-center">
                    <Icon className={`mx-auto mb-2 ${getScoreColor(score)}`} size={24} />
                    <div className={`text-2xl font-bold ${getScoreColor(score)} mb-1`}>
                      {score}
                    </div>
                    <div className="text-xs text-gray-400">{label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Detailed Scores Section */}
        <section className="py-16 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              Performance Breakdown
            </h2>
            <div className="space-y-6">
              {scoreItems.map(({ key, label, icon: Icon }) => {
                const score = offer.scores[key as keyof typeof offer.scores];
                const percentage = score;
                return (
                  <div key={key} className="glass-card p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Icon className={getScoreColor(score)} size={24} />
                        <h3 className="text-xl font-bold text-white">{label}</h3>
                      </div>
                      <div className={`text-3xl font-bold ${getScoreColor(score)}`}>
                        {score}/100
                      </div>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-4 overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r from-primary-green to-primary-lightgreen transition-all duration-1000`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Bonus Details */}
        {offer.bonusDetails && (
          <section className="py-16 bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-4xl font-bold text-white mb-12 text-center">
                Bonus Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-card p-8 text-center border-2 border-primary-green/30 hover:border-primary-green transition-colors">
                  <div className="text-5xl font-bold text-primary-green mb-3">
                    {offer.bonusDetails.matchPercent}%
                  </div>
                  <p className="text-gray-400 font-medium">Match Percentage</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Your deposit matched at this rate
                  </p>
                </div>
                <div className="glass-card p-8 text-center border-2 border-primary-green/30 hover:border-primary-green transition-colors">
                  <div className="text-5xl font-bold text-white mb-3">
                    £{offer.bonusDetails.maxBonus}
                  </div>
                  <p className="text-gray-400 font-medium">Maximum Bonus</p>
                  <p className="text-sm text-gray-500 mt-2">
                    The highest bonus you can receive
                  </p>
                </div>
                <div className="glass-card p-8 text-center border-2 border-accent-red/30 hover:border-accent-red transition-colors">
                  <div className="text-5xl font-bold text-accent-red mb-3">
                    {offer.bonusDetails.wagering}x
                  </div>
                  <p className="text-gray-400 font-medium">Wagering Requirement</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {offer.bonusDetails.wagering === 0 
                      ? 'No wagering required - withdraw anytime'
                      : `Times you must bet before withdrawal`
                    }
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Payout Methods */}
        {offer.payoutMethods && (
          <section className="py-16 bg-black">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-4xl font-bold text-white mb-12 text-center">
                Withdrawal Methods
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {offer.payoutMethods.map((method, index) => (
                  <div
                    key={index}
                    className="glass-card p-6 hover:border-primary-green transition-colors"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-white">{method.method}</h3>
                      <FiClock className="text-primary-green" size={20} />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-400">Processing Time</p>
                        <p className="text-primary-green font-bold">{method.speed}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Minimum Amount</p>
                        <p className="text-white font-bold">£{method.minAmount}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Pros and Cons */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-white mb-12 text-center">
              What We Like & What We Don't
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass-card p-8 border-l-4 border-primary-green">
                <h3 className="text-3xl font-bold text-primary-green mb-6 flex items-center">
                  <FiCheck className="mr-3" size={32} />
                  Pros
                </h3>
                <ul className="space-y-4">
                  {offer.pros.map((pro, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FiCheck className="text-primary-green flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-300 text-lg">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass-card p-8 border-l-4 border-accent-red">
                <h3 className="text-3xl font-bold text-accent-red mb-6 flex items-center">
                  <FiX className="mr-3" size={32} />
                  Cons
                </h3>
                <ul className="space-y-4">
                  {offer.cons.map((con, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FiX className="text-accent-red flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-300 text-lg">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Verdict */}
        <section className="py-16 bg-black">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card p-10 border-2 border-primary-green/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary-green rounded-full flex items-center justify-center">
                  <FiStar className="text-black" size={24} />
                </div>
                <h2 className="text-4xl font-bold text-primary-green">Our Verdict</h2>
              </div>
              
              <div className="space-y-6 text-gray-300 leading-relaxed text-lg">
                <p>
                  <strong className="text-white">{offer.name}</strong> delivers a{' '}
                  {offer.rating10 >= 9 ? 'outstanding' : offer.rating10 >= 8 ? 'strong' : 'solid'}{' '}
                  experience for UK players with a {offer.rating10}/10 rating. 
                  {offer.bonusDetails && offer.bonusDetails.wagering === 0 
                    ? ' The standout feature is zero wagering requirements, making it one of the most player-friendly options available. You can withdraw your winnings immediately without meeting any betting conditions.'
                    : ` The ${offer.bonusDetails?.matchPercent || 100}% match bonus up to £${offer.bonusDetails?.maxBonus || 100} is competitive, though the ${offer.bonusDetails?.wagering || 35}x wagering requirement requires careful consideration. Make sure you understand how much you'll need to wager before the bonus becomes withdrawable.`
                  }
                </p>
                <p>
                  Payout speeds are {offer.scores.payoutSpeed >= 90 ? 'excellent' : offer.scores.payoutSpeed >= 80 ? 'strong' : 'reasonable'}, 
                  with {offer.payoutMethods?.[0]?.speed || 'standard processing times'} for the fastest method. 
                  The trust score of {offer.scores.trustScore}/100 reflects {offer.scores.trustScore >= 90 ? 'outstanding' : 'solid'} licensing and security measures, giving you confidence in the platform's reliability.
                </p>
                <p>
                  {offer.pros.length > offer.cons.length 
                    ? 'Overall, the pros significantly outweigh the cons, making this a recommended choice for UK players looking for a trustworthy casino with competitive bonuses and reliable payouts.'
                    : 'While there are some limitations, the overall package offers good value for most players, especially those who prioritize the specific strengths this casino provides.'
                  }
                </p>
              </div>

              <div className="mt-10 pt-8 border-t border-primary-green/30">
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={offer.officialUrl}
                    target="_blank"
                    rel="noreferrer noopener sponsored"
                    className="btn-primary flex-1 inline-flex items-center justify-center text-lg py-4"
                  >
                    Claim Your Bonus Now
                    <FiExternalLink className="ml-2" />
                  </a>
                  <Link
                    to="/compare"
                    className="btn-secondary flex-1 inline-flex items-center justify-center text-lg py-4"
                  >
                    Compare with Other Casinos
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
